import React, { useState, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Text, Box, HStack, Icon, Divider, useToast, Badge,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiImage, FiTrash2 } from 'react-icons/fi';
import axiosInstance from '../../../api/AxiosInstance';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  taller: any;
  onGuardado: () => void;
}

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
const MAX_FOTOS = 5;
const MAX_PDFS = 2;

const ModalEvidenciasTaller: React.FC<Props> = ({ isOpen, onClose, taller, onGuardado }) => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const fotoInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();

  const fotosExistentes = (taller?.archivos || []).filter((a: any) => a.tipoArchivo === 'foto');
  const pdfsExistentes = (taller?.archivos || []).filter((a: any) => a.tipoArchivo === 'evidencia_pdf');

  const [nuevasFotos, setNuevasFotos] = useState<File[]>([]);
  const [nuevosPdfs, setNuevosPdfs] = useState<File[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [archivoAEliminar, setArchivoAEliminar] = useState<any>(null);
  const [eliminando, setEliminando] = useState(false);

  const totalFotos = fotosExistentes.length + nuevasFotos.length;
  const totalPdfs = pdfsExistentes.length + nuevosPdfs.length;

  const handleCambioFotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    const disponibles = MAX_FOTOS - totalFotos;
    if (disponibles <= 0) {
      toast({ title: `Máximo ${MAX_FOTOS} fotos`, status: 'warning', duration: 3000 });
      return;
    }
    setNuevasFotos((p) => [...p, ...files].slice(0, p.length + disponibles));
    e.target.value = '';
  };

  const handleCambioPdfs = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type === 'application/pdf');
    const disponibles = MAX_PDFS - totalPdfs;
    if (disponibles <= 0) {
      toast({ title: `Máximo ${MAX_PDFS} PDFs`, status: 'warning', duration: 3000 });
      return;
    }
    setNuevosPdfs((p) => [...p, ...files].slice(0, p.length + disponibles));
    e.target.value = '';
  };

  const handleGuardar = async () => {
    if (nuevasFotos.length === 0 && nuevosPdfs.length === 0) {
      toast({ title: 'Sin archivos nuevos', status: 'info', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const formData = new FormData();
      nuevasFotos.forEach((f) => formData.append('fotos', f));
      nuevosPdfs.forEach((f) => formData.append('pdfs', f));
      await axiosInstance.post(`/cultura/talleres/${taller.id}/evidencias`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Evidencias guardadas', status: 'success', duration: 3000 });
      setNuevasFotos([]);
      setNuevosPdfs([]);
      onGuardado();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const handleConfirmarEliminar = async () => {
    if (!archivoAEliminar) return;
    setEliminando(true);
    try {
      await axiosInstance.delete(`/cultura/talleres/archivo/${archivoAEliminar.id}`);
      toast({ title: 'Archivo eliminado', status: 'success', duration: 2000 });
      onCloseEliminar();
      onGuardado();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setEliminando(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Evidencias — {taller?.titulo}
          </ModalHeader>
          <ModalBody py={5}>
            <VStack spacing={5} align="stretch">
              <HStack spacing={3}>
                <Badge colorScheme={totalFotos >= MAX_FOTOS ? 'red' : 'green'} px={3} py={1} borderRadius="full">
                  Fotos: {totalFotos}/{MAX_FOTOS}
                </Badge>
                <Badge colorScheme={totalPdfs >= MAX_PDFS ? 'red' : 'green'} px={3} py={1} borderRadius="full">
                  PDFs: {totalPdfs}/{MAX_PDFS}
                </Badge>
              </HStack>

              {/* Fotografías */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" color="#5b182b" mb={2}>Fotografías (máx. {MAX_FOTOS})</Text>
                {fotosExistentes.length > 0 && (
                  <Box mb={3}>
                    <Text fontSize="xs" color="gray.500" mb={2}>Subidas anteriormente:</Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {fotosExistentes.map((a: any) => (
                        <Box key={a.id} position="relative" w="80px" h="80px">
                          <Box w="80px" h="80px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200">
                            <img src={`${API}${a.rutaArchivo}`} alt={a.nombreArchivo}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </Box>
                          <Button size="xs" position="absolute" top="2px" right="2px" colorScheme="red"
                            borderRadius="full" p={0} minW="18px" h="18px"
                            onClick={() => { setArchivoAEliminar(a); onOpenEliminar(); }}>
                            <Icon as={FiTrash2} boxSize={2.5} />
                          </Button>
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                )}
                {nuevasFotos.length > 0 && (
                  <Box mb={3}>
                    <Text fontSize="xs" color="gray.500" mb={2}>Nuevas (pendientes):</Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {nuevasFotos.map((f, i) => (
                        <Box key={i} position="relative" w="80px" h="80px">
                          <Box w="80px" h="80px" borderRadius="md" overflow="hidden" border="2px dashed"
                            borderColor="green.300" bg="green.50" display="flex" alignItems="center" justifyContent="center">
                            <Icon as={FiImage} boxSize={6} color="green.500" />
                          </Box>
                          <Button size="xs" position="absolute" top="2px" right="2px" colorScheme="red"
                            borderRadius="full" p={0} minW="18px" h="18px"
                            onClick={() => setNuevasFotos((p) => p.filter((_, j) => j !== i))}>
                            <Icon as={FiTrash2} boxSize={2.5} />
                          </Button>
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                )}
                <input ref={fotoInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleCambioFotos} />
                <Button size="sm" leftIcon={<Icon as={FiUpload} />} variant="outline" colorScheme="teal"
                  onClick={() => fotoInputRef.current?.click()} isDisabled={totalFotos >= MAX_FOTOS}>
                  {totalFotos >= MAX_FOTOS ? 'Límite alcanzado' : 'Agregar fotos'}
                </Button>
              </Box>

              <Divider />

              {/* PDFs */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" color="#5b182b" mb={2}>PDFs de Evidencia (máx. {MAX_PDFS})</Text>
                {pdfsExistentes.length > 0 && (
                  <VStack align="start" spacing={1} mb={3}>
                    {pdfsExistentes.map((a: any) => (
                      <HStack key={a.id} justify="space-between" w="full">
                        <HStack>
                          <Icon as={FiFile} color="red.500" />
                          <Text fontSize="sm">{a.nombreArchivo}</Text>
                        </HStack>
                        <Button size="xs" colorScheme="red" variant="ghost"
                          onClick={() => { setArchivoAEliminar(a); onOpenEliminar(); }}>
                          <Icon as={FiTrash2} />
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                )}
                {nuevosPdfs.length > 0 && (
                  <VStack align="start" spacing={1} mb={3}>
                    {nuevosPdfs.map((f, i) => (
                      <HStack key={i} justify="space-between" w="full">
                        <HStack>
                          <Icon as={FiFile} color="green.500" />
                          <Text fontSize="sm">{f.name}</Text>
                          <Badge colorScheme="green" fontSize="xs">Nuevo</Badge>
                        </HStack>
                        <Button size="xs" colorScheme="red" variant="ghost"
                          onClick={() => setNuevosPdfs((p) => p.filter((_, j) => j !== i))}>
                          <Icon as={FiTrash2} />
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                )}
                <input ref={pdfInputRef} type="file" accept="application/pdf" multiple style={{ display: 'none' }} onChange={handleCambioPdfs} />
                <Button size="sm" leftIcon={<Icon as={FiUpload} />} variant="outline" colorScheme="red"
                  onClick={() => pdfInputRef.current?.click()} isDisabled={totalPdfs >= MAX_PDFS}>
                  {totalPdfs >= MAX_PDFS ? 'Límite alcanzado' : 'Agregar PDFs'}
                </Button>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="outline" onClick={onClose} isDisabled={guardando}>Cerrar</Button>
            <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar}
              isLoading={guardando} loadingText="Guardando...">
              Guardar evidencias
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#5b182b">Eliminar archivo</AlertDialogHeader>
            <AlertDialogBody>
              ¿Eliminar <strong>{archivoAEliminar?.nombreArchivo}</strong>? Esta acción no se puede deshacer.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={eliminando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleConfirmarEliminar} ml={3}
                isLoading={eliminando} loadingText="Eliminando...">
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ModalEvidenciasTaller;
